import { Button, TextField, Typography, useTheme } from '@mui/material';
import { useAuth } from '../../../../hooks/useAuth';
import { MouseEvent } from 'react';
import FallbackSpinner from '../../../../materio/@core/components/spinner';
import ReactFlow, { useReactFlow, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import { useRef, useState } from 'react';
import './node-designer.css';
import useChats from '../../hooks/useChats';
import SimpleDialog from '../../../common/SimpleDialog';
import { DirectLine } from 'botframework-directlinejs';
import ReactWebChat from 'botframework-webchat';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import ImageNode from '../node-types/ImageNode';
import Designer from './Designer';
import MiniDrawer from './ChatDesignerNavBar';
import ChatDesignerFlowPropertiesModal from './ChatDesignerFlowPropertiesModal';
import UtteranceEditableList from './UtteranceEditableList';
import ImportUtterancesModal from './ImportUtterancesModal';
import ConfirmDiscardChanges from '../../../../components/ConfirmDiscardChanges';
import useAwaitableComponent from '../../../../hooks/useAwaitableComponent';


type Props = {
    chatId: string,
    open: boolean
    onClose: () => void
}

const ChatDesigner = ({ chatId, onClose, open }: Props) => {
    const [directLine, setDirectLine] = useState(new DirectLine({ token: '-kqNPC4Cgts.NvKsckZnSpBI-1fmMXlNW_7hYtXnIyszq48Kyft7ZDs' }));
    const { chat, saveChat, updateChatName, updateChatIntent, saving, isDirty } = useChats();
    const [propertiesOpen, setPropertiesOpen] = useState(false);
    const [utterancesOpen, setUtterancesOpen] = useState(false);
    const [importUtterancesOpen, setImportUtterancesOpen] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);
    const [status, execute, resolve, reject, reset] = useAwaitableComponent();    
    const showModal = status !== "idle";

    const { user } = useAuth();
    const theme = useTheme();
    const [errors, setErrors] = useState({
        nameError: false,
        intentError: false
    })
  
    if (!user) return <FallbackSpinner />

    const onSave = async () => {
        setShouldSave(false);
        setPropertiesOpen(false);

        let temp = {
            nameError: chat.name === '' || !chat.name,
            intentError: chat.intent === '' || !chat.intent
        }

        if (temp.nameError || temp.intentError) {
            setErrors(temp);
            setPropertiesOpen(true);
            setShouldSave(true)
            return;
        }

        await saveChat();

    }

    const onCloseProperties = () => {
        setShouldSave(false);
        setPropertiesOpen(false);
    }

    const onClosePropertiesWithSave = () => {
        if (shouldSave) {
            onSave();
        }
        else {
            setPropertiesOpen(false);
        }
    }

    const handleClose = async () => {
        if (isDirty) {
            try {
                const value = await execute();
                onClose();
            } catch (err) {

            } finally {
                reset();
            }
        }
        else {
            onClose();
        }
    }

    return (
        <SimpleDialog open={open} onClose={handleClose} fullScreen sx={{ padding: 0 }}>
            <MiniDrawer
                onClose={handleClose}
                openProperties={() => setPropertiesOpen(true)}
                openUtterances={() => setUtterancesOpen(true)}
                openImportUtterances={() => setImportUtterancesOpen(true)}
                onSave={onSave}
                saving={saving}

            />
            <div className='node-designer' style={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%', borderTop: `1px solid lightgray`, maxHeight: '100%', overflowY: 'auto', margin: 0 }}>
                <div style={{ width: '100%', display: 'flex' }}>
                    <ReactFlowProvider>
                        <Designer onClose={onClose} />
                    </ReactFlowProvider>
                </div>
                <div >
                    <ReactWebChat
                        directLine={directLine}
                        userID={user.id}
                        styleOptions={{
                            botAvatarImage: '',
                            botAvatarBackgroundColor: theme.palette.background.paper,
                            backgroundColor: theme.palette.background.default,
                            bubbleBackground: theme.palette.background.paper,
                            bubbleTextColor: 'white',
                        }}
                    />
                </div>
            </div>

            {propertiesOpen && <ChatDesignerFlowPropertiesModal open={propertiesOpen} onClose={onCloseProperties} onOkay={onClosePropertiesWithSave} errors={errors} />}
            {utterancesOpen && <UtteranceEditableList open={utterancesOpen} onClose={() => setUtterancesOpen(false)} />}
            {importUtterancesOpen && <ImportUtterancesModal open={importUtterancesOpen} onClose={() => setImportUtterancesOpen(false)} />}
            <ConfirmDiscardChanges showDialog={showModal } confirmNavigation={resolve} cancelNavigation={reject} />

        </SimpleDialog>
    )
}

export { ChatDesigner }

export default ChatDesigner;